/**
 * 테마 제어 패널 도움말 하이라이트 기능
 *
 * 물음표 버튼 클릭 시 해당 항목으로 스크롤하고 하이라이트 효과를 제공합니다.
 */

// ThemeHelpHighlight 객체
export const ThemeHelpHighlight = {
  // 기본 설정
  defaultThemeColor: '#e97730',
  blinkInterval: 300, // 깜빡임 간격 (ms)
  maxBlinks: 3, // 깜빡임 횟수 (테마색 -> 투명 -> 테마색 -> 원래)
  panelScrollOffset: 20, // 패널 내부 스크롤 여유 공간 (px)
  pageScrollOffset: 100, // 실제 화면 스크롤 여유 공간 (px)
  isHighlighting: false, // 하이라이트 효과 진행 중 여부

  /**
   * 테마 색상 가져오기
   * @returns {string} 테마 색상 HEX 코드
   */
  getThemeColor: function() {
    try {
      const themeColor = getComputedStyle(document.documentElement).getPropertyValue('--theme-color-primary').trim();
      return themeColor || this.defaultThemeColor;
    } catch (e) {
      return this.defaultThemeColor;
    }
  },

  /**
   * HEX 색상을 RGB로 변환
   * @param {string} hex - HEX 색상 코드 (#rrggbb)
   * @returns {string} RGB 색상 코드 (r, g, b)
   */
  hexToRgb: function(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      const r = parseInt(result[1], 16);
      const g = parseInt(result[2], 16);
      const b = parseInt(result[3], 16);
      return r + ', ' + g + ', ' + b;
    }
    return '233, 119, 48'; // 기본값 (#e97730)
  },

  /**
   * 요소의 원래 스타일 저장
   * @param {HTMLElement} element - 대상 요소
   * @returns {object} 원래 스타일 객체
   */
  saveOriginalStyles: function(element) {
    return {
      border: element.style.border || '',
      borderColor: element.style.borderColor || '',
      borderWidth: element.style.borderWidth || '',
      borderStyle: element.style.borderStyle || '',
      transition: element.style.transition || '',
      borderRadius: element.style.borderRadius || '',
      boxShadow: element.style.boxShadow || ''
    };
  },

  /**
   * 요소의 원래 스타일 복원
   * @param {HTMLElement} element - 대상 요소
   * @param {object} originalStyles - 저장된 원래 스타일 객체
   */
  restoreOriginalStyles: function(element, originalStyles) {
    if (!element || !originalStyles) return;

    try {
      // 요소가 DOM에 존재하는지 확인
      if (!element.parentNode && element !== document.body && element !== document.documentElement) {
        return;
      }

      // border 속성 복원
      // 먼저 모든 border 관련 속성을 완전히 제거
      element.style.border = '0';
      element.style.borderWidth = '0';
      element.style.borderStyle = 'none';
      element.style.borderColor = 'transparent';
      // removeProperty로도 제거 시도 (더 확실하게)
      element.style.removeProperty('border');
      element.style.removeProperty('border-width');
      element.style.removeProperty('border-style');
      element.style.removeProperty('border-color');

      // 원본 border 속성이 있으면 복원
      if (originalStyles.border !== undefined && originalStyles.border !== '' && originalStyles.border !== null) {
        element.style.border = originalStyles.border;
      } else {
        // 원본 border 속성이 없거나 빈 문자열이면 border를 완전히 제거
        // 이미 위에서 제거했으므로 추가 작업 불필요
        // 단, 원본에 개별 속성들이 있었던 경우만 복원

        // borderColor 속성 복원 (원본에 있었던 경우만)
        if (originalStyles.borderColor !== undefined && originalStyles.borderColor !== '' && originalStyles.borderColor !== null) {
          element.style.borderColor = originalStyles.borderColor;
        }

        // borderWidth 속성 복원 (원본에 있었던 경우만)
        if (originalStyles.borderWidth !== undefined && originalStyles.borderWidth !== '' && originalStyles.borderWidth !== null) {
          element.style.borderWidth = originalStyles.borderWidth;
        }

        // borderStyle 속성 복원 (원본에 있었던 경우만)
        if (originalStyles.borderStyle !== undefined && originalStyles.borderStyle !== '' && originalStyles.borderStyle !== null) {
          element.style.borderStyle = originalStyles.borderStyle;
        }
      }

      // transition 속성 복원
      if (originalStyles.transition !== undefined) {
        if (originalStyles.transition === '' || originalStyles.transition === null) {
          element.style.removeProperty('transition');
        } else {
          element.style.transition = originalStyles.transition;
        }
      }

      // boxShadow 속성 복원
      if (originalStyles.boxShadow !== undefined) {
        if (originalStyles.boxShadow === '' || originalStyles.boxShadow === null) {
          element.style.removeProperty('box-shadow');
        } else {
          element.style.boxShadow = originalStyles.boxShadow;
        }
      }

      // borderRadius 속성 복원
      if (originalStyles.borderRadius !== undefined) {
        if (originalStyles.borderRadius === '' || originalStyles.borderRadius === null) {
          element.style.removeProperty('border-radius');
        } else {
          element.style.borderRadius = originalStyles.borderRadius;
        }
      }
    } catch (e) {
      console.warn('스타일 복원 중 오류:', e);
    }
  },

  /**
   * 모든 물음표 버튼 비활성화
   */
  disableAllHelpButtons: function() {
    try {
      const helpButtons = document.querySelectorAll('.theme-help-btn');
      helpButtons.forEach(function(btn) {
        btn.disabled = true;
        btn.style.opacity = '0.5';
        btn.style.cursor = 'not-allowed';
        btn.style.pointerEvents = 'none';
      });
    } catch (e) {
      console.warn('물음표 버튼 비활성화 중 오류:', e);
    }
  },

  /**
   * 모든 물음표 버튼 활성화
   */
  enableAllHelpButtons: function() {
    try {
      const helpButtons = document.querySelectorAll('.theme-help-btn');
      helpButtons.forEach(function(btn) {
        btn.disabled = false;
        btn.style.opacity = '';
        btn.style.cursor = '';
        btn.style.pointerEvents = '';
      });
    } catch (e) {
      console.warn('물음표 버튼 활성화 중 오류:', e);
    }
  },

  /**
   * 깜빡임 효과 적용
   * @param {HTMLElement} element - 대상 요소
   * @param {string} themeColor - 테마 색상
   * @param {object} originalStyles - 원래 스타일 객체
   * @param {boolean} useBoxShadow - box-shadow 사용 여부
   * @param {number} delay - 시작 지연 시간 (ms)
   */
  applyBlinkEffect: function(element, themeColor, originalStyles, useBoxShadow, delay) {
    if (!element || !originalStyles) return;

    const self = this;
    let blinkCount = 0;
    let timeoutId = null;
    let rafId = null;
    const rgb = this.hexToRgb(themeColor);
    const maxDuration = (delay || 200) + (this.maxBlinks * this.blinkInterval) + 1000; // 최대 지속 시간 (여유 시간 포함)

    // 하이라이트 효과 시작: 버튼 비활성화
    this.isHighlighting = true;
    this.disableAllHelpButtons();

    // 안전장치: 최대 지속 시간 후 강제로 스타일 복원 및 버튼 활성화
    const safetyTimeout = setTimeout(function() {
      try {
        // 모든 타이머 정리
        if (timeoutId) clearTimeout(timeoutId);
        if (rafId) cancelAnimationFrame(rafId);
        if (element && element.parentNode) {
          self.restoreOriginalStyles(element, originalStyles);
        }
        // 하이라이트 효과 종료: 버튼 활성화
        self.isHighlighting = false;
        self.enableAllHelpButtons();
      } catch (e) {
        console.warn('하이라이트 효과 복원 중 오류:', e);
        // 오류 발생 시에도 버튼 활성화
        if (timeoutId) clearTimeout(timeoutId);
        if (rafId) cancelAnimationFrame(rafId);
        self.isHighlighting = false;
        self.enableAllHelpButtons();
      }
    }, maxDuration);

    function blink() {
      try {
        // 요소가 DOM에 존재하는지 확인
        if (!element || !element.parentNode) {
          clearTimeout(safetyTimeout);
          if (timeoutId) clearTimeout(timeoutId);
          if (rafId) cancelAnimationFrame(rafId);
          self.restoreOriginalStyles(element, originalStyles);
          // 하이라이트 효과 종료: 버튼 활성화
          self.isHighlighting = false;
          self.enableAllHelpButtons();
          return;
        }

        if (blinkCount < self.maxBlinks) {
          if (blinkCount === 0) {
            // 첫 번째: 테마 색상
            element.style.borderColor = themeColor;
            if (useBoxShadow) {
              element.style.boxShadow = '0 0 20px rgba(' + rgb + ', 0.5)';
            }
          } else if (blinkCount === 1) {
            // 두 번째: 투명하게
            element.style.borderColor = 'transparent';
            if (useBoxShadow) {
              element.style.boxShadow = '0 0 0px rgba(' + rgb + ', 0)';
            }
          } else if (blinkCount === 2) {
            // 세 번째: 다시 테마 색상
            element.style.borderColor = themeColor;
            if (useBoxShadow) {
              element.style.boxShadow = '0 0 20px rgba(' + rgb + ', 0.5)';
            }
          }
          blinkCount++;
          rafId = requestAnimationFrame(function() {
            timeoutId = setTimeout(blink, self.blinkInterval);
          });
        } else {
          // 원래대로 복원
          clearTimeout(safetyTimeout);
          if (timeoutId) clearTimeout(timeoutId);
          if (rafId) cancelAnimationFrame(rafId);
          self.restoreOriginalStyles(element, originalStyles);
          // 하이라이트 효과 종료: 버튼 활성화
          self.isHighlighting = false;
          self.enableAllHelpButtons();
        }
      } catch (e) {
        console.warn('깜빡임 효과 적용 중 오류:', e);
        // 오류 발생 시에도 스타일 복원
        try {
          if (element && element.parentNode) {
            self.restoreOriginalStyles(element, originalStyles);
          }
        } catch (restoreError) {
          console.warn('스타일 복원 중 오류:', restoreError);
        }
        clearTimeout(safetyTimeout);
        if (timeoutId) clearTimeout(timeoutId);
        if (rafId) cancelAnimationFrame(rafId);
        // 하이라이트 효과 종료: 버튼 활성화
        self.isHighlighting = false;
        self.enableAllHelpButtons();
      }
    }

    // 깜빡임 시작
    try {
      rafId = requestAnimationFrame(function() {
        timeoutId = setTimeout(blink, delay || 200);
      });
    } catch (e) {
      console.warn('깜빡임 효과 시작 중 오류:', e);
      // 오류 발생 시 즉시 스타일 복원
      try {
        if (element && element.parentNode) {
          self.restoreOriginalStyles(element, originalStyles);
        }
      } catch (restoreError) {
        console.warn('스타일 복원 중 오류:', restoreError);
      }
      clearTimeout(safetyTimeout);
      // 하이라이트 효과 종료: 버튼 활성화
      this.isHighlighting = false;
      this.enableAllHelpButtons();
    }
  },

  /**
   * 패널 내부 요소로 스크롤하고 하이라이트 효과 적용
   * @param {HTMLElement} targetElement - 타겟 요소
   * @param {HTMLElement} contentArea - 스크롤 가능한 컨테이너 요소
   */
  scrollAndHighlightPanel: function(targetElement, contentArea) {
    if (!targetElement || !contentArea) return;

    let originalStyles = null;

    try {
      const themeColor = this.getThemeColor();

      // 요소로 스크롤 (패널 내부 스크롤)
      const contentRect = contentArea.getBoundingClientRect();
      const elementRect = targetElement.getBoundingClientRect();

      // 요소의 상대적 위치 계산
      const elementTopRelative = elementRect.top - contentRect.top + contentArea.scrollTop;
      const elementBottomRelative = elementTopRelative + targetElement.offsetHeight;
      const scrollTop = contentArea.scrollTop;
      const scrollBottom = scrollTop + contentArea.clientHeight;

      // 요소가 보이지 않으면 스크롤
      if (elementTopRelative < scrollTop || elementBottomRelative > scrollBottom) {
        contentArea.scrollTo({
          top: elementTopRelative - this.panelScrollOffset,
          behavior: 'smooth'
        });
      }

      // 원래 스타일 저장
      originalStyles = this.saveOriginalStyles(targetElement);

      // 초기 border 설정
      targetElement.style.transition = 'border-color 0.3s ease';
      targetElement.style.border = '2px solid ' + themeColor;
      targetElement.style.borderRadius = '4px';

      // 깜빡임 효과 적용
      this.applyBlinkEffect(targetElement, themeColor, originalStyles, false, 200);
    } catch (e) {
      console.warn('패널 내부 스크롤 및 하이라이트 효과 적용 중 오류:', e);
      // 오류 발생 시 스타일 복원
      if (targetElement && originalStyles) {
        try {
          this.restoreOriginalStyles(targetElement, originalStyles);
        } catch (restoreError) {
          console.warn('스타일 복원 중 오류:', restoreError);
        }
      }
    }
  },

  /**
   * 실제 화면 요소로 스크롤하고 하이라이트 효과 적용
   * @param {HTMLElement} targetElement - 타겟 요소 (실제 화면의 요소)
   */
  scrollAndHighlightPage: function(targetElement) {
    if (!targetElement) return;

    let originalStyles = null;

    try {
      const themeColor = this.getThemeColor();

      // 실제 화면으로 스크롤
      const elementRect = targetElement.getBoundingClientRect();
      const elementTop = window.scrollY + elementRect.top;

      window.scrollTo({
        top: elementTop - this.pageScrollOffset,
        behavior: 'smooth'
      });

      // 원래 스타일 저장
      originalStyles = this.saveOriginalStyles(targetElement);

      // 초기 border 및 box-shadow 설정
      targetElement.style.transition = 'border-color 0.3s ease, box-shadow 0.3s ease';
      targetElement.style.border = '3px solid ' + themeColor;
      targetElement.style.borderRadius = '4px';
      targetElement.style.boxShadow = '0 0 20px rgba(' + this.hexToRgb(themeColor) + ', 0.5)';

      // 깜빡임 효과 적용
      this.applyBlinkEffect(targetElement, themeColor, originalStyles, true, 500);
    } catch (e) {
      console.warn('실제 화면 요소 스크롤 및 하이라이트 효과 적용 중 오류:', e);
      // 오류 발생 시 스타일 복원
      if (targetElement && originalStyles) {
        try {
          this.restoreOriginalStyles(targetElement, originalStyles);
        } catch (restoreError) {
          console.warn('스타일 복원 중 오류:', restoreError);
        }
      }
    }
  },

  /**
   * targetId와 실제 화면 요소 selector 매핑
   * selector는 함수로 정의하여 동적으로 요소를 찾을 수 있음
   * @type {Object.<string, Function>}
   */
  targetIdToPageElementSelector: {
    'nav-layout-control-section': function() {
      return '#nav-layout-left, #nav-layout-right, .header-bottom';
    },
    'menu-scroll-type-wrapper': function() {
      return '.header-bottom .main-menu';
    },
    'banner-type-control-section': function() {
      return '#banner-container';
    },
    'color-control-section': function() {
      return 'body';
    },
    'header-top-control-section': function() {
      return '.header-top';
    },
    'header-bottom-control-section': function() {
      return '.header-bottom, .nav-layout';
    },
    'font-control-section': function() {
      return 'body';
    },
    'sidebar-menu-type-control-section': function() {
      return '.sidebar-menu-wrapper';
    },
    'sidebar-columns-control-section': function() {
      // 업무화면 가장 테두리의 .row 요소 찾기
      return '.sidebar-menu-wrapper';
    },
    'mega-menu-section-max-columns-control-section': function() {
      return '.header-bottom .mega-menu-section';
    },
    'mega-menu-columns-control-section': function() {
      return '.header-bottom .mega-menu';
    },
    'right-sidebar-type-control-section': function() {
      return '.header-action-wrap';
    },
    'footer-theme-control-section': function() {
      return '.footer-area';
    },
    'related-sites-control-section': function() {
      return '.related-sites-section';
    },
    'copyright-alignment-control-section': function() {
      return '.footer-bottom.copyright';
    },
    'grid-gap-control-section': function() {
      return '.row .col-12';
    },
    'container-width-control-section': function() {
      // 업무화면 최상단 .container .row 찾기
      return 'body';
    },
    'area-container-width-header-top': function() {
      return '.header-top .container';
    },
    'area-container-width-header-bottom': function() {
      return '.header-bottom .container';
    },
    'area-container-width-banner': function() {
      return '#banner-container';
    },
    'area-container-width-content': function() {
      return '.main-wrapper .container.my-4';
    },
    'area-container-width-footer': function() {
      return '.footer-area .container';
    }
  },

  /**
   * selector로 요소 찾기
   * @param {string|null} selector - CSS selector (쉼표로 구분된 여러 selector 지원)
   * @returns {HTMLElement|NodeList|null} 찾은 요소 또는 요소 목록 (클래스 selector인 경우 NodeList)
   */
  findElementBySelector: function(selector) {
    if (!selector) return null;

    try {
      // 쉼표로 구분된 여러 selector 지원
      const selectors = selector.split(',').map(function(s) {
        return s.trim();
      });

      const foundElements = [];

      for (let i = 0; i < selectors.length; i++) {
        const sel = selectors[i];
        // ID selector인 경우
        if (sel.startsWith('#')) {
          const id = sel.substring(1);
          const element = document.getElementById(id);
          if (element) {
            foundElements.push(element);
          }
        } else if (sel.startsWith('.')) {
          // 클래스 selector인 경우: 모든 요소 찾기
          const elements = document.querySelectorAll(sel);
          if (elements && elements.length > 0) {
            // NodeList를 배열로 변환하여 추가
            for (let j = 0; j < elements.length; j++) {
              foundElements.push(elements[j]);
            }
          }
        } else {
          // 일반 selector인 경우: 클래스가 포함되어 있는지 확인
          if (sel.includes('.')) {
            // 클래스가 포함된 selector인 경우 모든 요소 찾기
            const elements = document.querySelectorAll(sel);
            if (elements && elements.length > 0) {
              for (let j = 0; j < elements.length; j++) {
                foundElements.push(elements[j]);
              }
            }
          } else {
            // ID나 클래스가 아닌 일반 selector인 경우 첫 번째 요소만
            const element = document.querySelector(sel);
            if (element) {
              foundElements.push(element);
            }
          }
        }
      }

      // 결과 반환
      if (foundElements.length === 0) {
        return null;
      } else if (foundElements.length === 1) {
        return foundElements[0];
      } else {
        // 여러 요소인 경우 배열로 반환
        return foundElements;
      }
    } catch (e) {
      console.warn('요소 찾기 중 오류:', e);
      return null;
    }
  },

  /**
   * 테마 제어 패널 내 항목과 실제 화면 요소 하이라이트 처리 (공통 로직)
   * @param {string} targetId - 타겟 요소 ID
   * @param {HTMLElement} contentArea - 테마 제어 패널의 콘텐츠 영역
   * @param {HTMLElement|Array|null} pageElement - 실제 화면의 요소 또는 요소 배열 (null이면 패널 내부만 하이라이트)
   */
  highlightControlAndPageElement: function(targetId, contentArea, pageElement) {
    // 테마 제어 패널 내 항목 찾기
    const controlSectionElement = document.getElementById(targetId);

    // 테마 제어 패널 내 항목으로 스크롤 및 하이라이트
    if (controlSectionElement && contentArea) {
      this.scrollAndHighlightPanel(controlSectionElement, contentArea);
    }

    // 실제 화면 요소가 있으면 하이라이트
    if (pageElement) {
      // 배열인 경우 모든 요소에 대해 하이라이트
      if (Array.isArray(pageElement)) {
        pageElement.forEach(function(element) {
          if (element) {
            this.scrollAndHighlightPage(element);
          }
        }.bind(this));
      } else {
        // 단일 요소인 경우
        this.scrollAndHighlightPage(pageElement);
      }
    }
  },

  /**
   * 물음표 버튼 클릭 이벤트 처리
   * @param {HTMLElement} button - 클릭된 버튼 요소
   * @param {HTMLElement} contentArea - 테마 제어 패널의 콘텐츠 영역
   */
  handleHelpButtonClick: function(button, contentArea) {
    // 이미 하이라이트 효과가 진행 중이면 무시
    if (this.isHighlighting) {
      return;
    }

    const targetId = button.getAttribute('data-target');
    if (!targetId) return;

    // targetId에 해당하는 selector 함수가 있는지 확인
    const selectorFunc = this.targetIdToPageElementSelector[targetId];

    let pageElement = null;
    if (selectorFunc) {
      // selector 함수가 있으면 실행하여 selector 가져오기
      const selector = selectorFunc();
      // selector로 실제 화면 요소 찾기
      pageElement = this.findElementBySelector(selector);
    }

    // 공통 로직으로 하이라이트 처리
    this.highlightControlAndPageElement(targetId, contentArea, pageElement);
  },

  /**
   * 물음표 버튼 클릭 이벤트 초기화
   * @param {HTMLElement} contentArea - 테마 제어 패널의 콘텐츠 영역
   */
  initHelpButtons: function(contentArea) {
    if (!contentArea) return;

    const self = this;
    const helpButtons = contentArea.querySelectorAll('.theme-help-btn');

    helpButtons.forEach(function(btn) {
      // 기존 이벤트 리스너 제거 (중복 방지)
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);

      // 새로운 이벤트 리스너 등록
      newBtn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        self.handleHelpButtonClick(newBtn, contentArea);
      });
    });
  }
};

// 전역 객체로도 노출 (하위 호환성)
if (typeof window !== 'undefined') {
  window.ThemeHelpHighlight = ThemeHelpHighlight;
}

export default ThemeHelpHighlight;

